import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { getShopLogoUrl } from "@/utils/shopLogos";
import { shopClickUrl } from "@/lib/shopClickUrl";

interface ShopListingInfo {
  id: string;
  shopName: string;
  price: number | null;
  listingName?: string | null;
}

interface SuggestedPriceInfo {
  price: number;
  newPrice: number;
  shopName: string;
}

interface PriceDetailsPopoverProps {
  shopListings: ShopListingInfo[];
  suggestedPriceInfo: SuggestedPriceInfo;
  condition: string;
  onSelectPrice: (price: number) => void;
}

const CONDITION_LABELS: Record<string, string> = {
  new: "Nauja",
  like_new: "Labai gera",
  good: "Gera",
  acceptable: "Patenkinama",
};

const CONDITION_MULTIPLIERS: Record<string, number> = {
  new: 0.6,
  like_new: 0.5,
  good: 0.4,
  acceptable: 0.3,
};

function roundSuggestedPrice(price: number): number {
  if (price >= 10) return Math.round(price);
  return Math.round(price * 2) / 2;
}

export function getConditionAdjustedPrice(
  newPrice: number,
  condition: string,
): number {
  const multiplier = CONDITION_MULTIPLIERS[condition] ?? 0.5;
  return roundSuggestedPrice(newPrice * multiplier);
}

function getShopColor(name: string): string {
  const colors = [
    "bg-blue-100 text-blue-700",
    "bg-green-100 text-green-700",
    "bg-purple-100 text-purple-700",
    "bg-orange-100 text-orange-700",
    "bg-pink-100 text-pink-700",
    "bg-teal-100 text-teal-700",
  ];
  let hash = 0;
  for (const char of name) hash = char.charCodeAt(0) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function formatPrice(price: number): string {
  return price % 1 === 0 ? `${price}` : price.toFixed(2);
}

export function PriceDetailsPopover({
  shopListings,
  suggestedPriceInfo,
  condition,
  onSelectPrice,
}: PriceDetailsPopoverProps) {
  const [open, setOpen] = useState(false);

  if (shopListings.length === 0) return null;

  const isNotNew = condition !== "new";
  const adjustedPrice = getConditionAdjustedPrice(
    suggestedPriceInfo.newPrice,
    condition,
  );

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-blue-600"
        title="Kainos informacija"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Kainos pagrindimas"
        size="sm"
      >
        <div className="space-y-4">
          {/* Suggested price summary */}
          <div className="flex items-center justify-between rounded-lg bg-blue-50 p-3">
            <div>
              <p className="text-xs text-blue-600">Siūloma kaina</p>
              <p className="text-lg font-bold text-blue-900">
                {formatPrice(adjustedPrice)} &euro;
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                onSelectPrice(adjustedPrice);
                setOpen(false);
              }}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Naudoti
            </button>
          </div>

          {/* Condition hint */}
          {isNotNew && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex gap-2">
                <svg
                  className="mt-0.5 h-4 w-4 shrink-0 text-amber-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-xs text-amber-800">
                  Siūloma kaina sumažinta, nes žaidimas pažymėtas kaip{" "}
                  <strong>
                    &bdquo;{CONDITION_LABELS[condition] || condition}&ldquo;
                  </strong>
                  , o parduotuvėse kainos nurodytos naujiems žaidimams.
                </p>
              </div>
            </div>
          )}

          {/* Shop listings */}
          <div>
            <p className="mb-2 text-xs font-medium text-gray-500">
              Kainos parduotuvėse (naujas)
            </p>
            <div className="space-y-2">
              {shopListings.map((listing) => (
                <a
                  key={listing.id}
                  href={shopClickUrl(listing.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-lg border border-gray-100 p-2.5 transition-colors hover:bg-gray-50"
                >
                  {getShopLogoUrl(listing.shopName) ? (
                    <img
                      src={getShopLogoUrl(listing.shopName)!}
                      alt={listing.shopName}
                      className="h-8 w-8 shrink-0 rounded-lg object-contain"
                    />
                  ) : (
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${getShopColor(listing.shopName)}`}
                    >
                      {listing.shopName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {listing.shopName}
                    </p>
                    {listing.listingName && (
                      <p className="truncate text-xs text-gray-400">
                        {listing.listingName}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {listing.price != null
                        ? `${listing.price.toFixed(2)} \u20AC`
                        : "\u2014"}
                    </p>
                  </div>
                  <svg
                    className="h-4 w-4 shrink-0 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
